import { useCallback, useMemo } from 'react';

import { useGetPresetGroup } from '@restDataAccess';
import { AddOrUpdatePresetRequest } from '@scaleaq/sitecontrol-lib';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledCreatableMultiSelect } from '@/components';

const emptyGroupNames: string[] = [];

type PresetGroupSelectionFiledProps = {
  control: Control<AddOrUpdatePresetRequest>;
  setValue: UseFormSetValue<AddOrUpdatePresetRequest>;
};

export const PresetGroupSelectionFiled = ({
  control,
  setValue,
}: PresetGroupSelectionFiledProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['cameraControl', 'common']);
  const { data: presetGroupsData, isPending } = useGetPresetGroup({});

  const newPresetGroupNames = useWatch({ control, name: 'newPresetGroupNames' }) ?? emptyGroupNames;

  const options = useMemo(() => {
    const groups = presetGroupsData ?? [];
    return groups
      .map(group => ({ label: group.name, value: group.id }))
      .sort((left, right) => left.label.localeCompare(right.label, language));
  }, [language, presetGroupsData]);

  const handleCreatedGroupNamesChange = useCallback(
    (newPresetGroupNames: string[]) => {
      setValue('newPresetGroupNames', newPresetGroupNames, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <ControlledCreatableMultiSelect
      control={control}
      createdValues={newPresetGroupNames}
      description={t('preset.groupsDescription')}
      disabled={isPending}
      isLoading={isPending}
      label={t('preset.addToGroup')}
      name='presetGroupIds'
      onCreatedValuesChange={handleCreatedGroupNamesChange}
      options={options}
      placeholder={t('preset.groupsPlaceholder')}
    />
  );
};
